import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

export type DensityMode = 'auto' | 'compact' | 'comfortable' | 'spacious';

interface DensityConfig {
  // Table density
  tableCellPadding: string;
  tableRowHeight: string;
  tableHeaderHeight: string;
  
  // Button density
  buttonPaddingX: string;
  buttonPaddingY: string;
  buttonHeight: string;
  
  // General spacing
  cardPadding: string;
  sectionGap: string;
  
  // Typography
  baseFontSize: string;
  
  // Scale factor (para referencia)
  scale: number;
}

const DENSITY_CONFIGS: Record<Exclude<DensityMode, 'auto'>, DensityConfig> = {
  compact: {
    tableCellPadding: 'p-1',
    tableRowHeight: 'h-7',
    tableHeaderHeight: 'h-7',
    buttonPaddingX: 'px-2',
    buttonPaddingY: 'py-1',
    buttonHeight: 'h-6',
    cardPadding: 'p-2',
    sectionGap: 'gap-2',
    baseFontSize: 'text-xs',
    scale: 0.78,
  },
  comfortable: {
    tableCellPadding: 'p-3',
    tableRowHeight: 'h-12',
    tableHeaderHeight: 'h-12',
    buttonPaddingX: 'px-3',
    buttonPaddingY: 'py-2',
    buttonHeight: 'h-9',
    cardPadding: 'p-4',
    sectionGap: 'gap-4',
    baseFontSize: 'text-sm',
    scale: 0.92,
  },
  spacious: {
    tableCellPadding: 'p-4',
    tableRowHeight: 'h-12',
    tableHeaderHeight: 'h-12',
    buttonPaddingX: 'px-4',
    buttonPaddingY: 'py-2',
    buttonHeight: 'h-10',
    cardPadding: 'p-6',
    sectionGap: 'gap-6',
    baseFontSize: 'text-base',
    scale: 1.0,
  },
};

interface UIDensityContextType {
  mode: DensityMode;
  setMode: (mode: DensityMode) => void;
  config: DensityConfig;
  actualMode: Exclude<DensityMode, 'auto'>; // El modo real aplicado
}

const UIDensityContext = createContext<UIDensityContextType | undefined>(undefined);

const STORAGE_KEY = 'ui-density-mode';

/**
 * Detecta el nivel de zoom del navegador y sugiere una densidad
 */
function detectOptimalDensity(): Exclude<DensityMode, 'auto'> {
  // Detectar zoom del navegador
  const devicePixelRatio = window.devicePixelRatio || 1;
  const visualViewportWidth = window.visualViewport?.width || window.innerWidth;
  const actualWidth = window.screen.width;
  
  // Detectar zoom aproximado
  const zoomLevel = (visualViewportWidth / actualWidth) * devicePixelRatio;
  
  // También considerar el ancho real disponible
  const viewportWidth = window.innerWidth;
  
  // Solo usar compact en pantallas MUY pequeñas o zoom MUY alto
  if (viewportWidth < 1024 || zoomLevel > 1.25) {
    return 'compact';
  }
  
  // Usar comfortable como predeterminado para la mayoría de casos
  if (viewportWidth < 1920 || zoomLevel > 0.85) {
    return 'comfortable';
  }
  
  // En pantallas muy grandes o zoom muy bajo, usar spacious
  return 'spacious';
}

export function UIDensityProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DensityMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as DensityMode) || 'auto';
  });

  const [actualMode, setActualMode] = useState<Exclude<DensityMode, 'auto'>>(() => {
    return mode === 'auto' ? detectOptimalDensity() : mode;
  });

  // Detectar cambios en el tamaño de ventana o zoom
  useEffect(() => {
    if (mode !== 'auto') {
      setActualMode(mode);
      return;
    }

    const handleResize = () => {
      setActualMode(detectOptimalDensity());
    };

    // Detectar inmediatamente
    handleResize();

    // Escuchar cambios
    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, [mode]);

  const setMode = (newMode: DensityMode) => {
    console.log('🔄 [UIDensity] Changing mode to:', newMode);
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
    
    if (newMode !== 'auto') {
      console.log('✅ [UIDensity] Setting actualMode to:', newMode);
      setActualMode(newMode);
    } else {
      const detected = detectOptimalDensity();
      console.log('✅ [UIDensity] Auto-detected mode:', detected);
      setActualMode(detected);
    }
  };

  const config = DENSITY_CONFIGS[actualMode];

  return (
    <UIDensityContext.Provider value={{ mode, setMode, config, actualMode }}>
      {children}
    </UIDensityContext.Provider>
  );
}

export function useUIDensity() {
  const context = useContext(UIDensityContext);
  if (!context) {
    throw new Error('useUIDensity must be used within UIDensityProvider');
  }
  return context;
}
